import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import { LogtoProvider } from '@logto/react';
import { ApolloProvider } from '@apollo/client/react';
import { QueryClientProvider } from '@tanstack/react-query';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
} from '@/components';
import { LogtoWrapper } from '@/components/LogtoWrapper';
import defaultSettings from '../config/defaultSettings';
import { logtoConfig } from '@/config/logto';
import { errorConfig } from './requestErrorConfig';
import { apolloClient } from '@/services/graphql/apollo';
import { queryClient } from '@/services/queryClient';
import '@ant-design/v5-patch-for-react-19';

const loginPath = '/user/login';

// 不需要用户信息的页面
const noAuthPages = [
  '/callback',
  loginPath,
  '/user/register',
  '/user/register-result'
];

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  loading?: boolean;
  currentUser?: any;
}> {
  const { location } = history;

  // 对于不需要认证的页面，不执行用户信息获取
  if (noAuthPages.includes(location.pathname)) {
    return {
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  let cachedUser: any = null;
  try {
    const cachedUserRaw = localStorage.getItem('current_user');
    cachedUser = cachedUserRaw ? JSON.parse(cachedUserRaw) : null;
  } catch (error) {
    console.error('Failed to parse cached user:', error);
  }

  return {
    settings: defaultSettings as Partial<LayoutSettings>,
    currentUser: cachedUser || undefined,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    avatarProps: {
      title: <AvatarName />,
      render: () => {
        return <AvatarDropdown />;
      },
    },
    waterMarkProps: {
      content: 'Sector0x',
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;

      // 对于不需要认证的页面，不进行重定向检查
      if (noAuthPages.includes(location.pathname)) {
        return;
      }

      // 如果没有 Token，重定向到登录页
      const hasToken = !!localStorage.getItem('logto_access_token');
      if (!hasToken) {
        history.push(loginPath);
      }
    },
    childrenRender: (children) => children,
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: '/api',
  ...errorConfig,
};

export const rootContainer = (container: React.ReactElement) => {
  return (
    <LogtoProvider config={logtoConfig}>
      <LogtoWrapper>
        <ApolloProvider client={apolloClient}>
          <QueryClientProvider client={queryClient}>
            {container}
          </QueryClientProvider>
        </ApolloProvider>
      </LogtoWrapper>
    </LogtoProvider>
  );
};
